type Pattern = { nrChars: number; regex: RegExp; normalize?: (input: string) => string }

export class AddressValidator {
  static normalizedCountryCode(countryCode: string) {
    if (countryCode) {
      const normalizedCountryCode = countryCode.toUpperCase()
      return normalizedCountryCode === 'UK' ? 'GB' : normalizedCountryCode
    }
    return countryCode
  }

  static normalizedPostalCode(countryCode: string, postalCode: string) {
    let normalizedPostalCode = countryCode === 'NL' ? postalCode.replace(/[\s.]/g, '').toUpperCase() : postalCode
    const normalizedCountryCode = this.normalizedCountryCode(countryCode)
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const pattern = PATTERNS[normalizedCountryCode]
    if (pattern && pattern.normalize) {
      normalizedPostalCode = pattern.normalize(normalizedPostalCode)
    }
    return normalizedPostalCode
  }

  static autoAddDash(postalCode: string, position: number, length: number) {
    const p = postalCode.indexOf('-')
    let normalizedPostalCode = postalCode
    if (p === -1 && normalizedPostalCode.length === length - 1) {
      normalizedPostalCode = `${normalizedPostalCode.substr(0, position)} - ${normalizedPostalCode.substr(position)}`
    }
    return normalizedPostalCode
  }

  static removeDash(postalCode: string) {
    let normalizedPostalCode = postalCode
    if (normalizedPostalCode) {
      normalizedPostalCode = normalizedPostalCode.replace('-', '')
    }
    return normalizedPostalCode
  }
}

const PATTERNS: Record<string, Pattern> = {
  AC: {
    nrChars: 8,
    regex: /^[ASCN]\\s{0,1}[1][Z][Z]$/,
  },
  AD: {
    nrChars: 5,
    regex: /^AD[1-7]0\d$/,
  },
  AF: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  AI: {
    nrChars: 7,
    regex: /^(?:AI-)?2640$/,
  },
  AL: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  AM: {
    nrChars: 4,
    regex: /^(?:37)?\d{4}$/,
  },
  AR: {
    nrChars: 4,
    regex: /^((?:[A-HJ-NP-Z])?\d{4})([A-Z]{3})?$/,
  },
  AS: {
    nrChars: 5,
    regex: /^(96799)(?:[-](\d{4}))?$/,
  },
  AT: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  AU: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  AX: {
    nrChars: 5,
    regex: /^22\d{3}$/,
  },
  AZ: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  BA: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  BB: {
    nrChars: 7,
    regex: /^BB\d{5}$/,
  },
  BD: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  BE: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  BG: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  BH: {
    nrChars: 3,
    regex: /^(?:\d|1[0-2])\d{2}$/,
  },
  BL: {
    nrChars: 5,
    regex: /^9[78][01]\d{2}$/,
  },
  BM: {
    nrChars: 4,
    regex: /^[A-Z]{2} ?[A-Z0-9]{2}$/,
  },
  BN: {
    nrChars: 6,
    regex: /^[A-Z]{2} ?\d{4}$/,
  },
  BR: {
    nrChars: 9,
    regex: /^\d{5}-?\d{3}$/,
  },
  BT: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  BY: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  CA: {
    nrChars: 7,
    regex: /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/,
  },
  CC: {
    nrChars: 4,
    regex: /^6799$/,
  },
  CH: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  CL: {
    nrChars: 7,
    regex: /^\d{7}$/,
  },
  CN: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  CO: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  CR: {
    nrChars: 4,
    regex: /^\d{4,5}|\d{3}-\d{4}$/,
  },
  CV: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  CX: {
    nrChars: 4,
    regex: /^6798$/,
  },
  CY: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  CZ: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  DE: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  DK: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  DO: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  DZ: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  EC: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  EE: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  EG: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  EH: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  ES: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  ET: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  FI: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  FK: {
    nrChars: 7,
    regex: /^FIQQ 1ZZ$/,
  },
  FM: {
    nrChars: 5,
    regex: /^(9694[1-4])(?:[-](\d{4}))?$/,
  },
  FO: {
    nrChars: 3,
    regex: /^\d{3}$/,
  },
  FR: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  GB: {
    nrChars: 5,
    regex:
      /^GIR[ ]?0AA|((AB|AL|B|BA|BB|BD|BH|BL|BN|BR|BS|BT|CA|CB|CF|CH|CM|CO|CR|CT|CV|CW|DA|DD|DE|DG|DH|DL|DN|DT|DY|E|EC|EH|EN|EX|FK|FY|G|GL|GY|GU|HA|HD|HG|HP|HR|HS|HU|HX|IG|IM|IP|IV|JE|KA|KT|KW|KY|L|LA|LD|LE|LL|LN|LS|LU|M|ME|MK|ML|N|NE|NG|NN|NP|NR|NW|OL|OX|PA|PE|PH|PL|PO|PR|RG|RH|RM|S|SA|SE|SG|SK|SL|SM|SN|SO|SP|SR|SS|ST|SW|SY|TA|TD|TF|TN|TQ|TR|TS|TW|UB|W|WA|WC|WD|WF|WN|WR|WS|WV|YO|ZE)(\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}))|BFPO[ ]?\d{1,4}$/,
  },
  GE: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  GF: {
    nrChars: 5,
    regex: /^9[78]3\d{2}$/,
  },
  GG: {
    nrChars: 6,
    regex: /^GIR[ ]?0AA|((GY)(\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}))|BFPO[ ]?\d{1,4}$/,
  },
  GI: {
    nrChars: 6,
    regex: /^GIR[ ]?0AA|((GX)(\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}))|BFPO[ ]?\d{1,4}$/,
  },
  GL: {
    nrChars: 4,
    regex: /^39\d{2}$/,
  },
  GN: {
    nrChars: 3,
    regex: /^\d{3}$/,
  },
  GP: {
    nrChars: 4,
    regex: /^9[78][01]\d{2}$/,
  },
  GR: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  GS: {
    nrChars: 8,
    regex: /^SIQQ 1ZZ$/,
  },
  GT: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  GU: {
    nrChars: 5,
    regex: /^(969(?:[12]\d|3[12]))(?:[-](\d{4}))?$/,
  },
  GW: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  HM: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  HN: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  HR: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  HT: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  HU: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  IC: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  ID: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  IL: {
    nrChars: 7,
    regex: /^\d{5}(?:\d{2})?$/,
  },
  IM: {
    nrChars: 8,
    regex: /^IM\d[\dA-Z]? ?\d[ABD-HJLN-UW-Z]{2}$/,
  },
  IN: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  IO: {
    nrChars: 8,
    regex: /^BBND 1ZZ$/,
  },
  IR: {
    nrChars: 5,
    regex: /^\d{5}-?\d{5}$/,
  },
  IS: {
    nrChars: 3,
    regex: /^\d{3}$/,
  },
  IT: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  IQ: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  JE: {
    nrChars: 12,
    regex: /^JE[1-5].*/,
  },
  JO: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  JP: {
    nrChars: 8,
    regex: /^\d{3}-?\d{4}$/,
  },
  KE: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  KG: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  KH: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  KW: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  KY: {
    nrChars: 8,
    regex: /^KY\d-\d{4}$/,
  },
  KZ: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  LA: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  LB: {
    nrChars: 4,
    regex: /^(?:\d{4})(?: ?(?:\d{4}))?$/,
  },
  LI: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  LK: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  LR: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  LS: {
    nrChars: 3,
    regex: /^\d{3}$/,
  },
  LT: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  LU: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  LV: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  MA: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  MC: {
    nrChars: 5,
    regex: /^980\d{2}$/,
  },
  MD: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  ME: {
    nrChars: 5,
    regex: /^8\d{4}$/,
  },
  MF: {
    nrChars: 5,
    regex: /^9[78][01]\d{2}$/,
  },
  MG: {
    nrChars: 3,
    regex: /^\d{3}$/,
  },
  MH: {
    nrChars: 10,
    regex: /^(969[67]\d)(?:[-](\d{4}))?$/,
  },
  MK: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  MM: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  MN: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  MP: {
    nrChars: 5,
    regex: /^(9695[012])(?:[-](\d{4}))?$/,
  },
  MQ: {
    nrChars: 5,
    regex: /^9[78]2\d{2}$/,
  },
  MT: {
    nrChars: 8,
    regex: /^[A-Z]{3} ?\d{2,4}$/,
  },
  MU: {
    nrChars: 5,
    regex: /^\d{3}(?:\d{2}|[A-Z]{2}\d{3})$/,
  },
  MV: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  MX: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  MY: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  MZ: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  NC: {
    nrChars: 5,
    regex: /^988\d{2}$/,
  },
  NE: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  NF: {
    nrChars: 4,
    regex: /^2899$/,
  },
  NG: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  NI: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  NL: {
    nrChars: 6,
    normalize: AddressValidator.removeDash,
    regex: /^\d{4}[A-Z]{2}$/,
  },
  NO: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  NP: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  NZ: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  OM: {
    nrChars: 3,
    regex: /^(?:PC )?\d{3}$/,
  },
  PE: {
    nrChars: 5,
    regex: /^(?:LIMA \d{1,2}|CALLAO 0?\d)|[0-2]\d{4}$/,
  },
  PF: {
    nrChars: 5,
    regex: /^987\d{2}$/,
  },
  PG: {
    nrChars: 3,
    regex: /^\d{3}$/,
  },
  PH: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  PK: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  PL: {
    nrChars: 6,
    normalize: (p) => AddressValidator.autoAddDash(p, 2, 6),
    regex: /^\d{2}-\d{3}$/,
  },
  PM: {
    nrChars: 5,
    regex: /^9[78]5\d{2}$/,
  },
  PN: {
    nrChars: 8,
    regex: /^PCRN 1ZZ$/,
  },
  PR: {
    nrChars: 5,
    regex: /^(00[679]\d{2})(?:[-](\d{4}))?$/,
  },
  PT: {
    nrChars: 8,
    normalize: (p) => AddressValidator.autoAddDash(p, 4, 8),
    regex: /^\d{4}-\d{3}$/,
  },
  PW: {
    nrChars: 5,
    regex: /^(969(?:39|40))(?:[-](\d{4}))?$/,
  },
  PY: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  RE: {
    nrChars: 5,
    regex: /^9[78]4\d{2}$/,
  },
  RO: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  RS: {
    nrChars: 5,
    regex: /^\d{5,6}$/,
  },
  RU: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  SA: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  SE: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  SG: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  SH: {
    nrChars: 8,
    regex: /^(?:ASCN|STHL) 1ZZ$/,
  },
  SI: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  SJ: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  SK: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  SM: {
    nrChars: 5,
    regex: /^4789\d$/,
  },
  SN: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  SO: {
    nrChars: 8,
    regex: /^[A-Z]{2} ?\d{5}$/,
  },
  SV: {
    nrChars: 7,
    regex: /^CP [1-3][1-7][0-2]\d$/,
  },
  SZ: {
    nrChars: 7,
    regex: /^[HLMS]\d{3}$/,
  },
  TA: {
    nrChars: 8,
    regex: /^TDCU 1ZZ$/,
  },
  TC: {
    nrChars: 8,
    regex: /^TKCA 1ZZ$/,
  },
  TH: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  TJ: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  TM: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  TN: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  TR: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  TW: {
    nrChars: 5,
    regex: /^\d{3}(?:\d{2})?$/,
  },
  TZ: {
    nrChars: 4,
    regex: /^\d{4,5}$/,
  },
  UA: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  UM: {
    nrChars: 5,
    regex: /^96898$/,
  },
  US: {
    nrChars: 5,
    regex: /^(\d{5})(?:[-](\d{4}))?$/,
  },
  UY: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
  UZ: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  VA: {
    nrChars: 5,
    regex: /^00120$/,
  },
  VC: {
    nrChars: 6,
    regex: /^VC\d{4}$/,
  },
  VE: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  VG: {
    nrChars: 6,
    regex: /^VG\d{4}$/,
  },
  VI: {
    nrChars: 10,
    regex: /^(008(?:(?:[0-4]\d)|(?:5[01])))(?:[-](\d{4}))?$/,
  },
  VN: {
    nrChars: 6,
    regex: /^\d{6}$/,
  },
  WF: {
    nrChars: 5,
    regex: /^986\d{2}$/,
  },
  XK: {
    nrChars: 5,
    regex: /^[1-7]\d{4}$/,
  },
  YT: {
    nrChars: 5,
    regex: /^976\d{2}$/,
  },
  ZA: {
    nrChars: 4,
    regex: /^\d{4}$/,
  },
  ZM: {
    nrChars: 5,
    regex: /^\d{5}$/,
  },
}
